import { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import PropTypes from "prop-types";
import { useFilters, usePagination, useSortBy, useTable } from "react-table";

import { dispatch } from "../../../state/store";
import { setMarketsRefresh } from "../../../state/ducks";

import { t } from "../../../services";
import { strCode } from "../../../consts";
import { useTableData } from "../../../hooks";

import {
  SmallButton,
  FlexContainers,
  Heading5,
  SearchInput,
  WrapperContainer,
  RefreshButton,
  Paragraph2,
  Table,
  TablePagination
} from "../../../components";
import { MarketsTableColumns } from "./MarketsTableColumns";
import { MarketsTableWrapper } from "./styled";

const MarketsTable = ({ marketsData, onMarketsOpening }) => {
  const [searchValue, setSearchValue] = useState("");

  const loading = useSelector((store) => store.markets?.loading);
  const error = useSelector((store) => store.markets?.error);

  const data = useTableData({ loading, data: marketsData });

  const columns = useMemo(() => MarketsTableColumns, []);
  const tableInstance = useTable(
    {
      columns,
      data,
      initialState: {
        sortBy: [
          {
            id: "market",
            asc: true
          }
        ]
      }
    },
    useFilters,
    useSortBy,
    usePagination
  );

  const {
    rows, // total rows
    setFilter,
    nextPage, // pagination props
    previousPage,
    canNextPage,
    canPreviousPage,
    gotoPage,
    pageCount,
    setPageSize,
    state: { pageIndex, pageSize }
  } = tableInstance;

  const handleFilterChange = (value) => {
    setFilter("market", value);
    setSearchValue(value);
  };

  return (
    <WrapperContainer width="100%">
      <WrapperContainer Container={FlexContainers.FlexRowBetween} mb={24} alignItems="start">
        <WrapperContainer Container={FlexContainers.FlexRowStart} width="100%" gap={16}>
          <Heading5>{t(strCode.MARKETS_TITLE)}</Heading5>

          <RefreshButton
            isLoading={loading}
            handleRefresh={() => dispatch(setMarketsRefresh(true))}
          />
        </WrapperContainer>

        <WrapperContainer Container={FlexContainers.FlexRowEnd} gap={8} width="100%">
          <WrapperContainer width="100%" maxWidth={329}>
            <SearchInput
              id="markets-search"
              value={searchValue}
              setValue={handleFilterChange}
              placeholder={t(strCode.SEARCH_MARKETS_PLACEHOLDER)}
            />
          </WrapperContainer>

          <SmallButton onClick={() => onMarketsOpening()} tooltipLabel={t(strCode.NEW_MARKET)}>
            {t(strCode.ADD_MARKET_TITLE)}
          </SmallButton>
        </WrapperContainer>
      </WrapperContainer>

      {error ? (
        <WrapperContainer
          Container={FlexContainers.FlexColumnCenter}
          width={"100%"}
          mt={200}
          textAlign={"center"}>
          <Paragraph2>{t(strCode.MARKETS_FETCH_ERROR)}</Paragraph2>
        </WrapperContainer>
      ) : (
        marketsData && (
          <>
            <MarketsTableWrapper>
              <Table
                tableKey="markets-table"
                tableInstance={tableInstance}
                searchedText={searchValue}
                searchInfo={`${t(strCode.NO_MARKET_CODE_FOUND)} '${searchValue}'.`}
                searchCheckInfo={t(strCode.CHECK_MARKET_CODE_MESSAGE)}
                onRowClick={onMarketsOpening}
                tooltipLabel={t(strCode.EDIT_MARKET)}
                isLoading={loading}
                hasPagination
              />
            </MarketsTableWrapper>

            <TablePagination
              tableKey="markets-table"
              tableSize={rows?.length}
              pageIndex={pageIndex}
              pageSize={pageSize}
              setPageSize={setPageSize}
              canPreviousPage={canPreviousPage}
              canNextPage={canNextPage}
              onPrevPage={previousPage}
              onNextPage={nextPage}
              onFirstPage={() => gotoPage(0)}
              onLastPage={() => gotoPage(pageCount - 1)}
            />
          </>
        )
      )}
    </WrapperContainer>
  );
};

MarketsTable.propTypes = {
  marketsData: PropTypes.arrayOf(
    PropTypes.shape({
      country: PropTypes.string.isRequired,
      market: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired
    })
  ),
  onMarketsOpening: PropTypes.func.isRequired
};

export default MarketsTable;
